import { useMemo } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { ExternalLink, Mail, Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { resolveKeptRef } from '@/components/keep/kept-ref';
import { useMailboxImportedRefs } from '@/hooks/useMemberExternalAccountQueries';
import { MailboxKeepActions } from './MailboxKeepActions';

type MailboxMessageCardItem = Extract<ConversationItem, { kind: 'mailbox_message_card' }>;

export interface ProvenanceMailboxMessageCardProps {
  item: MailboxMessageCardItem;
}

function fromLine(fromName: string | null, fromEmail: string | null): string | null {
  if (fromName && fromEmail) return `From ${fromName} <${fromEmail}>`;
  if (fromName) return `From ${fromName}`;
  if (fromEmail) return `From ${fromEmail}`;
  return null;
}

export function ProvenanceMailboxMessageCard({ item }: ProvenanceMailboxMessageCardProps) {
  const ids = useMemo(() => [item.providerMessageId], [item.providerMessageId]);
  const refsQuery = useMailboxImportedRefs(item.accountId, ids);
  const providerLabel = item.productLabel;
  const kept = resolveKeptRef(refsQuery.data, item.providerMessageId, item.importedContentId, item.importedCode);

  const from = fromLine(item.fromName, item.fromEmail);
  const date = item.receivedAt ? new Date(item.receivedAt).toLocaleDateString() : null;
  const metaParts = [from, date].filter((part): part is string => part !== null);
  const attachmentCount =
    typeof item.attachmentCount === 'number' ? item.attachmentCount : item.attachmentNames.length;
  const hiddenAttachmentCount = attachmentCount - item.attachmentNames.length;
  const attachmentLine =
    item.attachmentNames.length > 0
      ? hiddenAttachmentCount > 0
        ? `${item.attachmentNames.join(', ')} +${hiddenAttachmentCount} more`
        : item.attachmentNames.join(', ')
      : item.hasAttachments || attachmentCount > 0
        ? attachmentCount > 0
          ? `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`
          : 'Attachments'
        : null;

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 shrink-0" />
        <p className="min-w-0 truncate font-medium">{item.subject ?? '(no subject)'}</p>
        <Badge variant="outline" className="ml-auto shrink-0">
          {providerLabel}
        </Badge>
        {item.webLink ? (
          <button
            type="button"
            onClick={() => window.open(item.webLink as string, '_blank', 'noopener,noreferrer')}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
            aria-label={`Open in ${providerLabel}`}
            title={`Open in ${providerLabel}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      {metaParts.length > 0 ? (
        <p className="mt-1 min-w-0 truncate text-xs text-muted-foreground">{metaParts.join(' · ')}</p>
      ) : null}
      {item.toSummary ? <p className="min-w-0 truncate text-xs text-muted-foreground">To {item.toSummary}</p> : null}
      {item.folders.length > 0 ? (
        <p className="min-w-0 truncate text-xs text-muted-foreground">{item.folders.join(', ')}</p>
      ) : null}
      {attachmentLine ? (
        <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
          <Paperclip className="h-3 w-3 shrink-0" />
          <span className="min-w-0 truncate">{attachmentLine}</span>
        </p>
      ) : null}
      {item.authoredPreview ? (
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.authoredPreview}</p>
      ) : null}
      <div className="mt-2">
        <MailboxKeepActions scope="message" accountId={item.accountId} providerMessageIds={ids} kept={kept} />
      </div>
    </div>
  );
}
